import { BYTES_PER_GB, TRAINING_VIDEO_LIMITS, formatBytes, type TrainingVideoSettings } from '@ukinfo/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { VIDEO_SETTINGS_KEY, useVideoSettings, type VideoSettingsResponse } from './training/ExerciseVideos';
import { Button, Card, CardBody, ErrorBlock, Field, Input, LoadingBlock, SectionTitle, useToast } from './ui';

/**
 * Grenzen fuer Trainingsvideos: Groesse eines Videos, Laenge und Gesamtspeicher des Vereins.
 *
 * Die Grenzen stehen hier statt im Code, weil Platte und Mobilfunk von Verein zu Verein
 * verschieden sind. Der Server haelt die Bereiche ein (5-500 MB, 10-600 Sekunden, 1-200 GB).
 */

type Draft = { maxSizeMb: string; maxDurationSeconds: string; quotaGb: string };

const toDraft = (settings: TrainingVideoSettings): Draft => ({
  maxSizeMb: String(settings.maxSizeMb),
  maxDurationSeconds: String(settings.maxDurationSeconds),
  quotaGb: String(settings.quotaGb),
});

function rangeError(text: string, range: { min: number; max: number }, unit: string): string | undefined {
  const value = Number(text);
  if (text.trim() === '' || !Number.isInteger(value) || value < range.min || value > range.max) {
    return `Bitte eine ganze Zahl von ${range.min} bis ${range.max} ${unit} angeben.`;
  }
  return undefined;
}

export function TrainingVideoSettingsCard(): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const query = useVideoSettings();
  const [draft, setDraft] = useState<Draft | null>(null);

  // Erst wenn die gespeicherten Werte da sind, werden die Felder gefuellt.
  useEffect(() => {
    if (query.data) setDraft(toDraft(query.data.settings));
  }, [query.data]);

  const errors = draft ? {
    maxSizeMb: rangeError(draft.maxSizeMb, TRAINING_VIDEO_LIMITS.sizeMb, 'MB'),
    maxDurationSeconds: rangeError(draft.maxDurationSeconds, TRAINING_VIDEO_LIMITS.durationSeconds, 'Sekunden'),
    quotaGb: rangeError(draft.quotaGb, TRAINING_VIDEO_LIMITS.quotaGb, 'GB'),
  } : null;
  const invalid = !errors || Boolean(errors.maxSizeMb || errors.maxDurationSeconds || errors.quotaGb);

  const save = useMutation({
    mutationFn: () => api.patch<VideoSettingsResponse>('/api/settings/training-videos', {
      maxSizeMb: Number(draft?.maxSizeMb),
      maxDurationSeconds: Number(draft?.maxDurationSeconds),
      quotaGb: Number(draft?.quotaGb),
    }),
    onSuccess: async (saved) => {
      queryClient.setQueryData(VIDEO_SETTINGS_KEY, saved);
      toast.show('Einstellungen für Trainingsvideos gespeichert');
    },
    onError: (error: unknown) => toast.show(error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen', 'error'),
  });

  const usage = query.data?.usage;
  const set = (patch: Partial<Draft>): void => setDraft((current) => (current ? { ...current, ...patch } : current));

  return (
    <Card>
      <CardBody>
        <SectionTitle
          title="Trainingsvideos"
          subtitle="Wie groß und wie lang ein Video sein darf und wie viel Platz alle Videos zusammen belegen dürfen"
        />
        {query.isLoading && <LoadingBlock />}
        {query.error && <ErrorBlock error={query.error} />}
        {draft && errors && (
          <>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Maximale Videogröße (MB)" required error={errors.maxSizeMb} hint="5 bis 500 MB">
                {(id) => <Input id={id} type="number" inputMode="numeric" min={TRAINING_VIDEO_LIMITS.sizeMb.min} max={TRAINING_VIDEO_LIMITS.sizeMb.max} step={1} value={draft.maxSizeMb} onChange={(event) => set({ maxSizeMb: event.target.value })} />}
              </Field>
              <Field label="Maximale Videolänge (Sekunden)" required error={errors.maxDurationSeconds} hint="10 bis 600 Sekunden">
                {(id) => <Input id={id} type="number" inputMode="numeric" min={TRAINING_VIDEO_LIMITS.durationSeconds.min} max={TRAINING_VIDEO_LIMITS.durationSeconds.max} step={1} value={draft.maxDurationSeconds} onChange={(event) => set({ maxDurationSeconds: event.target.value })} />}
              </Field>
              <Field label="Speicher für Trainingsvideos gesamt (GB)" required error={errors.quotaGb} hint="1 bis 200 GB">
                {(id) => <Input id={id} type="number" inputMode="numeric" min={TRAINING_VIDEO_LIMITS.quotaGb.min} max={TRAINING_VIDEO_LIMITS.quotaGb.max} step={1} value={draft.quotaGb} onChange={(event) => set({ quotaGb: event.target.value })} />}
              </Field>
            </div>

            {usage && (
              <p className="mt-3 text-sm text-slate-600">
                Belegt: {formatBytes(usage.usedBytes)} von {formatBytes(usage.quotaBytes)} ({usage.videoCount} {usage.videoCount === 1 ? 'Video' : 'Videos'})
                {usage.quotaBytes > 0 && usage.usedBytes > Number(draft.quotaGb) * BYTES_PER_GB && Number.isFinite(Number(draft.quotaGb))
                  ? ' – mehr als das neue Kontingent: bestehende Videos bleiben, neue werden nicht angenommen.'
                  : ''}
              </p>
            )}
            <p className="hint mt-1">Die Grenzen gelten ab dem nächsten Hochladen. Am Handy genügt 720p, dann reichen meist 100 MB für gut zwei Minuten.</p>

            <Button className="mt-4" onClick={() => save.mutate()} loading={save.isPending} disabled={invalid}>
              Speichern
            </Button>
          </>
        )}
      </CardBody>
    </Card>
  );
}
